import {
  CompactDateInput,
  FieldWrapper,
  FilterSearchableSelect,
  FilterSelect,
  FilterToggle,
  dateChipLabel,
} from "./jobsFilterUtils";

export const JOBS_TIMESHEET_MODE_OPTIONS = [
  { value: "week", label: "Week" },
  { value: "day", label: "Day" },
];

export const JOBS_TIMESHEET_ALWAYS_DAYS_RANGE_OPTIONS = [
  { value: "1", label: "1 day" },
  { value: "2", label: "2 days" },
  { value: "3", label: "3 days" },
  { value: "4", label: "4 days" },
  { value: "5", label: "5 days" },
  { value: "6", label: "6 days" },
  { value: "7", label: "7 days" },
];

export const JOBS_TIMESHEET_WEEK_RANGE_OPTIONS = JOBS_TIMESHEET_ALWAYS_DAYS_RANGE_OPTIONS;
export const JOBS_TIMESHEET_DAY_RANGE_OPTIONS = JOBS_TIMESHEET_ALWAYS_DAYS_RANGE_OPTIONS;

export function JobsTimesheetsFilterFields({
  focusedFieldId,
  onFocusClear,
  filterClient,
  clientSearch,
  filterClientList = [],
  filterLocationList = [],
  filterLocations = [],
  filterMode,
  filterStartDate,
  filterRange,
  clientLoading,
  printWithoutValues,
  setFilterClient,
  setClientSearch,
  setFilterLocation,
  setFilterLocationList,
  setFilterLocations,
  setFilterMode,
  setFilterStartDate,
  setFilterRange,
  setPrintWithoutValues,
}) {
  const mode = filterMode || "day";

  const clientOptions = (filterClientList || []).map((client) => ({
    value: String(client.id),
    label: String(client.name || ""),
  }));

  const selectedClientLabel =
    clientOptions.find((o) => o.value === String(filterClient))?.label ||
    clientSearch ||
    "";

  return (
    <>
      <FieldWrapper fieldId="client" label="Client" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterSearchableSelect
          id="jobs-timesheets-client"
          value={filterClient || ""}
          selectedLabel={selectedClientLabel}
          loading={clientLoading}
          onQueryChange={setClientSearch}
          onChange={(clientId) => {
            if (!clientId) {
              setFilterClient("");
              setClientSearch("");
              setFilterLocation("");
              setFilterLocationList([]);
              setFilterLocations([]);
              return;
            }
            const client = (filterClientList || []).find(
              (c) => String(c.id) === String(clientId),
            );
            setFilterClient(String(clientId));
            setClientSearch(client?.name || "");
          }}
          options={clientOptions}
          placeholder="Search client..."
        />
      </FieldWrapper>

      <FieldWrapper fieldId="locations" label="Location" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <div className="min-h-[4.5rem] space-y-1">
          <div className="min-h-[1.25rem]">
            {filterLocations.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {filterLocations.map((locId) => {
                  const loc = filterLocationList.find((l) => String(l.id) === String(locId));
                  return (
                    <span key={locId} className="inline-flex max-w-full items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] text-gray-700">
                      <span className="truncate">{loc?.name || locId}</span>
                      <button
                        type="button"
                        onClick={() => setFilterLocations((prev) => prev.filter((id) => id !== locId))}
                        className="text-gray-400 hover:text-red-600"
                        aria-label={`Remove ${loc?.name || locId}`}
                      >
                        x
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : null}
          </div>
          <FilterSelect
            id="jobs-timesheets-location"
            value=""
            onChange={(val) => {
              if (val && !filterLocations.includes(val)) {
                setFilterLocations((prev) => [...prev, val]);
              }
            }}
            options={filterLocationList
              .filter((loc) => !filterLocations.includes(String(loc.id)))
              .map((loc) => ({ value: String(loc.id), label: loc.name }))}
            placeholder={filterClient ? "Add location..." : "Select client first"}
            disabled={!filterClient}
          />
        </div>
      </FieldWrapper>

      <FieldWrapper fieldId="mode" label="Mode" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterSelect
          id="jobs-timesheets-mode"
          value={mode}
          onChange={(value) => {
            setFilterMode(value);
            if (!filterRange) setFilterRange("7");
          }}
          options={JOBS_TIMESHEET_MODE_OPTIONS}
          placeholder={null}
        />
      </FieldWrapper>
      <FieldWrapper fieldId="startDate" label={mode === "week" ? "Week of" : "Start Date"} focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <CompactDateInput id="jobs-timesheets-start" value={filterStartDate} onChange={setFilterStartDate} />
      </FieldWrapper>
      <FieldWrapper fieldId="range" label="Range" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterSelect
          id="jobs-timesheets-range"
          value={filterRange || "7"}
          onChange={setFilterRange}
          options={JOBS_TIMESHEET_ALWAYS_DAYS_RANGE_OPTIONS}
          placeholder={null}
        />
      </FieldWrapper>
      <FieldWrapper fieldId="printWithoutValues" label="Print Options" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterToggle
          id="jobs-timesheets-print-without-values"
          checked={printWithoutValues || false}
          onChange={setPrintWithoutValues}
          label="Print without values"
        />
      </FieldWrapper>
    </>
  );
}

export function buildJobsTimesheetsFilterChips({
  clientSearch,
  filterLocations = [],
  filterLocationList = [],
  filterMode,
  filterStartDate,
  filterRange,
  printWithoutValues,
  today,
}) {
  const chips = [];
  if (clientSearch) chips.push({ id: "timesheets-client", key: "client", fieldId: "client", label: `Client: ${clientSearch}` });
  chips.push({ id: "timesheets-mode", key: "mode", fieldId: "mode", label: `Mode: ${filterMode === "week" ? "Week" : "Day"}` });
  chips.push({ id: "timesheets-start", key: "startDate", fieldId: "startDate", label: `Start Date: ${dateChipLabel(filterStartDate || today)}` });
  chips.push({
    id: "timesheets-range",
    key: "range",
    fieldId: "range",
    label: `Range: ${filterRange || "7"} ${(filterRange || "7") === "1" ? "day" : "days"}`,
  });
  if (printWithoutValues) chips.push({ id: "timesheets-print-without-values", key: "printWithoutValues", fieldId: "printWithoutValues", label: "Print without values" });
  filterLocations.forEach((locId) => {
    const locName = filterLocationList.find((l) => String(l.id) === String(locId))?.name || locId;
    chips.push({ id: `timesheets-location-${locId}`, key: "locations", value: locId, fieldId: "locations", label: `Location: ${locName}` });
  });
  return chips;
}
